import { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import AuthService from '../../utilities/services/auth.service'
import UserContext from '../../context/User/User'

import { checkPermission } from '../../utilities/helpers'
import {
  ORGANIZATION_PERMISSION_SLUGS,
  canViewAccountingReport,
} from '@blocktickets/shared-utils'

import {
  createNewFeature,
  getNewFeatureMedia,
  getNewFeatures,
  updateNewFeature,
  uploadNewFeatureMedia,
} from '../../utilities/api'

import Button from 'react-bootstrap/Button'
import Form from 'react-bootstrap/Form'

import {
  buildReportingMenuSections,
  reportingPathsFromSections,
} from '../../components/Navigation/reportingMenuSections'
import { Spinner } from '../../components/LoadingContainer/Spinner'
import { PageLoadingContainer } from '../../components/PageLoadingContainer'
import { FeedCard, TourStep } from '../../components/WhatsNew/FeatureViews'
import {
  filterUrlSuggestions,
  isUrlSearch,
  nextActionPath,
  suggestionsFor,
} from '../../components/WhatsNew/navRoutes'
import {
  AUDIENCES,
  AVAILABILITY_OPTIONS,
  WHERE_OPTIONS,
  checksPass,
  featureChecks,
  formatReleaseDate,
  guideMediaPath,
  previewRelease,
  sanitizeFolder,
  slugifyTitle,
} from '../../components/WhatsNew/whatsNewLogic'

import './whatsNewComposer.scss'

const NEW_FOLDER = '__new__'

const ALLOWED_FILE_TYPES = new Set(['mp4', 'webm', 'png', 'pdf'])

const composerState = ({
  form,
  selectedPaths,
  folderChoice,
  newFolderName,
  pendingFiles = [],
}) =>
  JSON.stringify({
    kind: form.kind,
    title: form.title,
    summary: form.summary,
    steps: form.steps,
    where: form.where,
    availability: form.availability,
    audiences: form.audiences,
    includeInTour: form.includeInTour,
    actionLabel: form.actionLabel,
    actionPath: form.actionPath,
    selectedPaths,
    folderChoice,
    newFolderName,
    pendingFiles: pendingFiles
      .filter((file) => file.included)
      .map((file) => `${file.folder}/${file.name}`),
  })

const releasePendingFiles = (files) => {
  files.forEach((file) => URL.revokeObjectURL(file.url))
}

const replaceSavedFeature = (releases, feature) => {
  const fields = { ...(feature || {}) }
  delete fields.release
  if (!fields.slug) return null
  let found = false
  const next = (releases || []).map((release) => {
    const features = release.features || []
    if (!features.some((entry) => entry.slug === fields.slug)) return release
    found = true
    return {
      ...release,
      features: features.map((entry) =>
        entry.slug === fields.slug ? { ...entry, ...fields } : entry
      ),
    }
  })
  return found ? next : null
}

const emptyForm = () => ({
  kind: 'new',
  title: '',
  summary: '',
  steps: [''],
  where: WHERE_OPTIONS[0],
  availability: AVAILABILITY_OPTIONS[0],
  audiences: [],
  includeInTour: true,
  actionLabel: '',
  actionPath: '',
})

const badgeKind = (name) => {
  const extension = String(name).split('.').pop().toLowerCase()
  if (extension === 'png') return 'png'
  if (extension === 'pdf') return 'pdf'
  return 'video'
}

const badgeLabel = (name) => String(name).split('.').pop().toUpperCase()

const folderFromPath = (filePath) => {
  const parts = String(filePath || '').split('/')
  return parts[2] || ''
}

export default function WhatsNewComposerPage() {
  const navigate = useNavigate()
  const { orgPermissions } = useContext(UserContext)
  const [searchParams] = useSearchParams()
  const featureSlug = searchParams.get('feature') || ''
  const fileInput = useRef(null)
  const appliedSlug = useRef('')
  const preserveMessage = useRef(false)
  const [form, setForm] = useState(emptyForm)
  const [editingSlug, setEditingSlug] = useState('')
  const [editingPublishedOn, setEditingPublishedOn] = useState('')
  const [loaded, setLoaded] = useState(false)
  const [releases, setReleases] = useState([])
  const [folders, setFolders] = useState([])
  const [folderChoice, setFolderChoice] = useState('')
  const [newFolderName, setNewFolderName] = useState('')
  const [selectedPaths, setSelectedPaths] = useState([])
  const [previewMode, setPreviewMode] = useState('tour')
  const [imageIndex, setImageIndex] = useState(0)
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState(null)
  const [pendingFiles, setPendingFiles] = useState([])
  const [savedState, setSavedState] = useState(null)
  const [pinnedQuery, setPinnedQuery] = useState('')
  const pendingFilesRef = useRef([])

  useEffect(() => {
    pendingFilesRef.current = pendingFiles
  }, [pendingFiles])

  useEffect(() => {
    return () => releasePendingFiles(pendingFilesRef.current)
  }, [])

  useEffect(() => {
    if (!AuthService.canManageWhatsNew()) navigate('/')
  }, [navigate])

  const fillFeature = (release, match) => {
    const nextForm = {
      kind: match.kind || 'new',
      title: match.title || '',
      summary: match.summary || '',
      steps: match.steps?.length ? match.steps : [''],
      where: match.where || WHERE_OPTIONS[0],
      availability: match.availability || AVAILABILITY_OPTIONS[0],
      audiences: match.audiences || [],
      includeInTour: match.includeInTour !== false,
      actionLabel: match.actionLabel || '',
      actionPath: match.actionPath || '',
    }
    const nextPaths = match.mediaPaths || []
    const nextFolder = folderFromPath(match.mediaPaths?.[0])
    appliedSlug.current = match.slug
    setEditingSlug(match.slug)
    setEditingPublishedOn(String(release.publishedOn || '').slice(0, 10))
    setForm(nextForm)
    setPinnedQuery('')
    setSelectedPaths(nextPaths)
    setFolderChoice(nextFolder)
    setNewFolderName('')
    setPendingFiles((current) => {
      releasePendingFiles(current)
      return []
    })
    setSavedState(
      composerState({
        form: nextForm,
        selectedPaths: nextPaths,
        folderChoice: nextFolder,
        newFolderName: '',
      })
    )
  }

  const load = async () => {
    try {
      const [featureResponse, mediaResponse] = await Promise.all([
        getNewFeatures(),
        getNewFeatureMedia(),
      ])
      const nextReleases = featureResponse?.data?.data || []
      setReleases(nextReleases)
      setFolders(mediaResponse?.data?.data || [])
      if (!appliedSlug.current && featureSlug) {
        const release = nextReleases.find((item) =>
          (item.features || []).some((entry) => entry.slug === featureSlug)
        )
        const match = release?.features?.find(
          (entry) => entry.slug === featureSlug
        )
        if (match) fillFeature(release, match)
      }
    } finally {
      setLoaded(true)
    }
  }

  useEffect(() => {
    if (!AuthService.canManageWhatsNew()) return
    load().catch(() =>
      setMessage({ variant: 'danger', text: 'Could not load features.' })
    )
  }, [])

  useEffect(() => {
    if (!loaded) return
    if (!featureSlug) {
      if (!appliedSlug.current) return
      appliedSlug.current = ''
      setEditingSlug('')
      setEditingPublishedOn('')
      setForm(emptyForm())
      setPinnedQuery('')
      setSelectedPaths([])
      setFolderChoice('')
      setNewFolderName('')
      setImageIndex(0)
      setSavedState(null)
      setPendingFiles((current) => {
        releasePendingFiles(current)
        return []
      })
      if (!preserveMessage.current) setMessage(null)
      preserveMessage.current = false
      return
    }
    const release = releases.find((item) =>
      (item.features || []).some((entry) => entry.slug === featureSlug)
    )
    const match = release?.features?.find((entry) => entry.slug === featureSlug)
    if (!match) {
      appliedSlug.current = ''
      setEditingSlug('')
      setEditingPublishedOn('')
      setForm(emptyForm())
      setPinnedQuery('')
      setSelectedPaths([])
      setFolderChoice('')
      setNewFolderName('')
      setPendingFiles((current) => {
        releasePendingFiles(current)
        return []
      })
      setMessage(null)
      navigate('/dev/whats-new', { replace: true })
      return
    }
    if (appliedSlug.current === featureSlug) return
    fillFeature(release, match)
    setMessage(null)
  }, [featureSlug, loaded, navigate, releases])

  const otherFeatures = useMemo(
    () =>
      releases.flatMap((release) =>
        (release.features || []).filter((entry) => entry.slug !== editingSlug)
      ),
    [editingSlug, releases]
  )
  const existingTitles = useMemo(
    () => otherFeatures.map((entry) => entry.title),
    [otherFeatures]
  )
  const existingSlugs = useMemo(
    () => otherFeatures.map((entry) => entry.slug),
    [otherFeatures]
  )
  const org = AuthService.getOrg()?.[0]
  const account = AuthService.getUser()
  const orgPermissionsReady = orgPermissions?.length > 0
  const isSuperUser = Boolean(
    account?.user?.uuid &&
    org?.members?.some(
      (member) => member?.uuid === account.user.uuid && member?.superAdmin
    )
  )
  const reportingPaths = reportingPathsFromSections(
    buildReportingMenuSections({
      foodAndBeverageEnabled: Boolean(org?.setting?.foodAndBeverage),
      orgUuid: org?.uuid,
      env: process.env.REACT_APP_STAGE || 'development',
      canViewAccounting: canViewAccountingReport({
        organization: org,
        isSuperAdmin: isSuperUser,
      }),
      canViewPayoutsReport:
        orgPermissionsReady &&
        checkPermission(
          AuthService.getPermissions(),
          ORGANIZATION_PERMISSION_SLUGS.VIEW_BALANCE_PAYOUTS
        ),
      paymentPlansEnabled: Boolean(org?.setting?.paymentPlans),
      isSuperUser,
    })
  )
  const navOptions = { reportingPaths, analyticsVisible: isSuperUser }
  const suggestions = suggestionsFor(form.where, navOptions)
  const urlSearch = isUrlSearch(suggestions)
  const filteredSuggestions = pinnedQuery
    ? filterUrlSuggestions(suggestions, pinnedQuery, false)
    : filterUrlSuggestions(suggestions, form.actionPath)
  const checks = featureChecks({
    input: {
      ...form,
      mediaPaths: selectedPaths,
      pendingFiles,
      suggestions,
    },
    existingTitles,
    existingSlugs,
  })
  const ready = checksPass(checks)
  const destination = editingSlug
    ? { publishedOn: editingPublishedOn, isNew: false }
    : previewRelease(releases)
  const typedFolder = sanitizeFolder(newFolderName)
  const matchedFolder = folders.find((folder) => folder.folder === typedFolder)
  const creating = folderChoice === NEW_FOLDER
  const activeFolder = creating
    ? matchedFolder?.folder || typedFolder
    : folderChoice
  const listedFiles = creating
    ? matchedFolder?.files || []
    : folders.find((folder) => folder.folder === folderChoice)?.files || []
  const listedKey = listedFiles.map((file) => file.path).join('|')

  useEffect(() => {
    if (!activeFolder) return
    const allowed = new Set(listedKey ? listedKey.split('|') : [])
    setSelectedPaths((current) => {
      const next = current.filter((filePath) => allowed.has(filePath))
      if (
        next.length === current.length &&
        next.every((filePath, index) => filePath === current[index])
      ) {
        return current
      }
      return next
    })
  }, [activeFolder, listedKey])

  const feature = {
    ...form,
    steps: form.steps.map((step) => step.trim()).filter(Boolean),
    mediaPaths: selectedPaths,
    slug: editingSlug || slugifyTitle(form.title),
  }

  const togglePath = (filePath) => {
    setSelectedPaths((current) =>
      current.includes(filePath)
        ? current.filter((item) => item !== filePath)
        : [...current, filePath]
    )
    setImageIndex(0)
  }

  const updateStep = (index, value) => {
    setForm((current) => ({
      ...current,
      steps: current.steps.map((step, stepIndex) =>
        stepIndex === index ? value : step
      ),
    }))
  }

  const stageFiles = (fileList) => {
    const folder = activeFolder
    if (!folder || !fileList?.length) return
    const incoming = Array.from(fileList)
    const invalid = incoming.some((file) => {
      const extension = file.name.split('.').pop().toLowerCase()
      return !ALLOWED_FILE_TYPES.has(extension)
    })
    if (invalid) {
      setMessage({
        variant: 'danger',
        text: 'Files must be .mp4, .webm, .png, or .pdf.',
      })
      return
    }
    setMessage(null)
    setPendingFiles((current) => {
      const next = [...current]
      incoming.forEach((file) => {
        const alreadyStaged = next.some(
          (item) => item.folder === folder && item.name === file.name
        )
        if (alreadyStaged) return
        next.push({
          id: `${folder}/${file.name}/${file.size}/${file.lastModified}`,
          name: file.name,
          file,
          folder,
          path: `/guides/${folder}/${file.name}`,
          url: URL.createObjectURL(file),
          included: true,
        })
      })
      return next
    })
    setImageIndex(0)
  }

  const togglePending = (id) => {
    setPendingFiles((current) =>
      current.map((file) =>
        file.id === id ? { ...file, included: !file.included } : file
      )
    )
    setImageIndex(0)
  }

  const currentState = composerState({
    form,
    selectedPaths,
    folderChoice,
    newFolderName,
    pendingFiles,
  })
  const unchanged =
    Boolean(editingSlug) && (savedState === null || savedState === currentState)

  const publish = async () => {
    if (!ready || saving || unchanged) return
    setSaving(true)
    setMessage(null)
    const wasNew = !editingSlug
    try {
      const queued = pendingFiles.filter((file) => file.included)
      const planned = queued.map((file) =>
        guideMediaPath(file.folder, file.name)
      )
      if (planned.some((filePath) => !filePath)) {
        setMessage({
          variant: 'danger',
          text: 'Could not save the folder and files.',
        })
        return
      }

      let mediaPaths = [...selectedPaths]
      try {
        const byFolder = new Map()
        queued.forEach((file) => {
          const group = byFolder.get(file.folder) || []
          group.push(file)
          byFolder.set(file.folder, group)
        })
        for (const [folder, files] of byFolder) {
          const body = new FormData()
          body.append('folder', folder)
          files.forEach((item) => body.append('files', item.file, item.name))
          const response = await uploadNewFeatureMedia(body)
          const saved = response?.data?.data?.files || []
          if (!saved.length || saved.some((file) => !file.path)) {
            throw new Error('files')
          }
          mediaPaths = Array.from(
            new Set([...mediaPaths, ...saved.map((file) => file.path)])
          )
        }
      } catch {
        setMessage({
          variant: 'danger',
          text: 'Could not save the folder and files.',
        })
        return
      }

      const payload = {
        ...form,
        slug: editingSlug,
        steps: form.steps,
        mediaPaths,
      }
      const wasEditing = Boolean(editingSlug)
      let savedFeature = null
      let failedMedia = []
      try {
        const saved = wasEditing
          ? await updateNewFeature(payload)
          : await createNewFeature(payload)
        savedFeature = saved?.data?.data || null
        failedMedia = saved?.data?.failedMedia || []
      } catch {
        setMessage({
          variant: 'danger',
          text: wasNew
            ? 'Could not add this feature to the feed.'
            : 'Could not save this feature.',
        })
        return
      }

      preserveMessage.current = true
      setMessage(
        failedMedia.length
          ? {
              variant: 'danger',
              text: 'Saved the feature, but an unchecked file is still in the guides folder. Close its preview and save again.',
            }
          : {
              variant: 'success',
              saved: Boolean(editingSlug),
              title: form.title.trim(),
              publishedOn: destination.publishedOn,
            }
      )
      appliedSlug.current = featureSlug
      setEditingSlug('')
      setEditingPublishedOn('')
      setForm(emptyForm())
      setPinnedQuery('')
      setSelectedPaths([])
      setFolderChoice('')
      setNewFolderName('')
      setPendingFiles((current) => {
        releasePendingFiles(current)
        return []
      })
      setImageIndex(0)
      if (featureSlug) navigate('/dev/whats-new', { replace: true })
      else preserveMessage.current = false
      if (wasEditing) {
        try {
          const mediaResponse = await getNewFeatureMedia()
          setFolders(mediaResponse?.data?.data || [])
        } catch {
          // The feature is already saved. The folder list refreshes on the next visit.
        }
      }
      const updatedReleases = wasEditing
        ? replaceSavedFeature(releases, savedFeature)
        : null
      if (updatedReleases) {
        setReleases(updatedReleases)
        window.dispatchEvent(
          new CustomEvent('whats-new:refresh', {
            detail: { releases: updatedReleases },
          })
        )
        return
      }
      try {
        const featureResponse = await getNewFeatures()
        const nextReleases = featureResponse?.data?.data || []
        setReleases(nextReleases)
        window.dispatchEvent(
          new CustomEvent('whats-new:refresh', {
            detail: { releases: nextReleases },
          })
        )
      } catch {
        window.dispatchEvent(new Event('whats-new:refresh'))
      }
    } finally {
      setSaving(false)
    }
  }

  const pathHeader = `/guides/${activeFolder ? `${activeFolder}/` : ''}`
  const failCount = checks.filter((check) => !check.pass).length
  const sameRelease = releases.find(
    (release) =>
      String(release.publishedOn || '').slice(0, 10) === destination.publishedOn
  )
  const releaseNote = editingSlug
    ? 'Keeps this feature on its original date'
    : !releases.length
      ? 'First publish creates this release'
      : sameRelease
        ? `Appends to today's release · ${sameRelease.features.length} already in it`
        : 'New day, so this opens the next release'
  const visiblePending = pendingFiles.filter(
    (file) =>
      file.folder === activeFolder &&
      !listedFiles.some((listed) => listed.path === file.path)
  )
  const includedPending = visiblePending.filter((file) => file.included)
  const showFileList = Boolean(folderChoice || creating)
  const previewSources = Object.fromEntries(
    includedPending.map((file) => [file.path, file.url])
  )
  const previewFeature = {
    ...feature,
    title: feature.title || 'Untitled feature',
    mediaPaths: [...selectedPaths, ...includedPending.map((file) => file.path)],
    previewSources,
  }

  if (!loaded || !AuthService.canManageWhatsNew()) {
    return <PageLoadingContainer style="without-sidebar" />
  }

  return (
    <div className="whats-new-composer">
      <div className="wn-form__bar">
        <span className="wn-form__accent" aria-hidden="true" />
        <h1>{editingSlug ? 'Edit feature' : 'New feature'}</h1>
        <span className="wn-form__dev">Developers only</span>
      </div>
      <div className="wn-form">
        <div className="wn-form__main">
          <section className="wn-card">
            <div className="wn-field">
              <div className="wn-field__label">Type</div>
              <div className="wn-pills">
                {[
                  ['new', 'New'],
                  ['improved', 'Improved'],
                ].map(([value, label]) => (
                  <Button
                    key={value}
                    type="button"
                    variant="light"
                    className={
                      form.kind === value ? 'wn-pill is-on' : 'wn-pill'
                    }
                    onClick={() =>
                      setForm((current) => ({ ...current, kind: value }))
                    }
                  >
                    {label}
                  </Button>
                ))}
              </div>
            </div>
            <div className="wn-field">
              <div className="wn-field__label">
                Title
                <span>{form.title.length}/60</span>
              </div>
              <Form.Control
                className="wn-input"
                value={form.title}
                maxLength={60}
                placeholder="e.g. Season ticket renewals"
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    title: event.target.value,
                  }))
                }
              />
            </div>
            <div className="wn-field">
              <div className="wn-field__label">
                Summary
                <span>{form.summary.length}/160</span>
              </div>
              <Form.Control
                className="wn-input"
                as="textarea"
                rows={2}
                value={form.summary}
                maxLength={160}
                placeholder="One sentence: what it does for the admin."
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    summary: event.target.value,
                  }))
                }
              />
            </div>
            <div className="wn-field">
              <div className="wn-field__label">How it works</div>
              {form.steps.map((step, index) => (
                <div key={`step-${index}`} className="wn-step">
                  <span className="wn-step__n">{index + 1}</span>
                  <Form.Control
                    className="wn-input"
                    value={step}
                    placeholder={
                      index === 0
                        ? 'e.g. Open Packages and choose Renewals.'
                        : 'Next step'
                    }
                    onChange={(event) => updateStep(index, event.target.value)}
                  />
                  {form.steps.length > 1 && (
                    <Button
                      type="button"
                      variant="light"
                      className="wn-step__remove"
                      aria-label="Remove step"
                      onClick={() =>
                        setForm((current) => ({
                          ...current,
                          steps: current.steps.filter(
                            (_, stepIndex) => stepIndex !== index
                          ),
                        }))
                      }
                    >
                      ×
                    </Button>
                  )}
                </div>
              ))}
              <Button
                type="button"
                variant="link"
                className="wn-add"
                onClick={() =>
                  setForm((current) => ({
                    ...current,
                    steps: [...current.steps, ''],
                  }))
                }
              >
                + Add step
              </Button>
            </div>
            <div className="wn-field">
              <div className="wn-field__label">
                Media
                <span>.mp4 .webm .png .pdf</span>
              </div>
              <p className="wn-media__help">
                Choose an existing folder or name a new one. Files stay in this
                form until you add or save the feature. They are stored in
                admin/public/guides. Unchecking a saved file removes it from
                that folder when you save, unless another feature still uses it.
              </p>
              <div className="wn-media__pick">
                <Form.Select
                  className="wn-input wn-input--mono"
                  aria-label="Guide folder"
                  value={folderChoice}
                  onFocus={() => {
                    getNewFeatureMedia()
                      .then((response) =>
                        setFolders(response?.data?.data || [])
                      )
                      .catch(() => {})
                  }}
                  onChange={(event) => {
                    setFolderChoice(event.target.value)
                    setNewFolderName('')
                  }}
                >
                  <option value="">Choose a folder</option>
                  {folders.map((folder) => (
                    <option key={folder.folder} value={folder.folder}>
                      {folder.folder} ({folder.files.length})
                    </option>
                  ))}
                  <option value={NEW_FOLDER}>+ New folder</option>
                </Form.Select>
                {creating && (
                  <Form.Control
                    className="wn-input wn-input--mono"
                    aria-label="New folder name"
                    placeholder="folder-name"
                    value={newFolderName}
                    onChange={(event) => setNewFolderName(event.target.value)}
                  />
                )}
              </div>
              {creating && matchedFolder && (
                <p className="wn-media__exists">
                  Folder already exists. Change the name of the new folder or
                  choose from the existing folder.
                </p>
              )}
              {showFileList && (
                <div className="wn-files">
                  <div className="wn-files__head">
                    <span>{pathHeader}</span>
                    <span>
                      {selectedPaths.length + includedPending.length
                        ? `${selectedPaths.length + includedPending.length} selected`
                        : 'None selected'}
                    </span>
                  </div>
                  {listedFiles.map((file) => {
                    const selectedIndex = selectedPaths.indexOf(file.path)
                    const selected = selectedIndex >= 0
                    return (
                      <Button
                        key={file.path}
                        type="button"
                        variant="light"
                        className={selected ? 'wn-file is-on' : 'wn-file'}
                        onClick={() => togglePath(file.path)}
                      >
                        <span className="wn-file__box">
                          {selected ? selectedIndex + 1 : ''}
                        </span>
                        <span className="wn-file__name">{file.name}</span>
                        <span
                          className={`wn-file__ext is-${badgeKind(file.name)}`}
                        >
                          {badgeLabel(file.name)}
                        </span>
                      </Button>
                    )
                  })}
                  {visiblePending.map((file) => {
                    const pendingIndex = includedPending.findIndex(
                      (item) => item.id === file.id
                    )
                    const selected = file.included
                    return (
                      <Button
                        key={file.id}
                        type="button"
                        variant="light"
                        className={selected ? 'wn-file is-on' : 'wn-file'}
                        onClick={() => togglePending(file.id)}
                      >
                        <span className="wn-file__box">
                          {selected
                            ? selectedPaths.length + pendingIndex + 1
                            : ''}
                        </span>
                        <span className="wn-file__name">{file.name}</span>
                        <span
                          className={`wn-file__ext is-${badgeKind(file.name)}`}
                        >
                          {badgeLabel(file.name)}
                        </span>
                      </Button>
                    )
                  })}
                  {!listedFiles.length && !visiblePending.length && (
                    <p className="wn-files__empty">
                      No files yet. They are saved when you add or save the
                      feature.
                    </p>
                  )}
                  <Button
                    type="button"
                    variant="light"
                    className="wn-files__upload"
                    disabled={!activeFolder}
                    onClick={() => {
                      const input = fileInput.current
                      if (!input) return
                      input.multiple = true
                      input.click()
                    }}
                  >
                    + Upload files
                  </Button>
                  <input
                    ref={fileInput}
                    className="wn-files__input"
                    type="file"
                    multiple
                    accept=".mp4,.webm,.png,.pdf,video/mp4,video/webm,image/png,application/pdf"
                    onChange={(event) => {
                      stageFiles(event.target.files)
                      event.target.value = ''
                    }}
                  />
                </div>
              )}
              {(selectedPaths.length > 0 || includedPending.length > 0) && (
                <div className="wn-stored">
                  <p>On the feature when you save</p>
                  {selectedPaths.map((filePath) => (
                    <div key={filePath}>{filePath}</div>
                  ))}
                  {includedPending.map((file) => (
                    <div key={file.id}>{file.name}</div>
                  ))}
                </div>
              )}
            </div>
          </section>
          <section className="wn-card">
            <div className="wn-field">
              <div className="wn-field__label">Where it lives</div>
              <Form.Select
                className="wn-input"
                value={form.where}
                onChange={(event) => {
                  const where = event.target.value
                  setPinnedQuery('')
                  setForm((current) => ({
                    ...current,
                    where,
                    actionPath: nextActionPath(
                      current.actionPath,
                      suggestionsFor(where, navOptions)
                    ),
                  }))
                }}
              >
                {WHERE_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </Form.Select>
            </div>
            <div className="wn-field">
              <div className="wn-field__label">Button</div>
              <Form.Control
                className="wn-input"
                value={form.actionLabel}
                placeholder="Open renewals"
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    actionLabel: event.target.value,
                  }))
                }
              />
              <Form.Control
                className="wn-input wn-input--mono"
                value={form.actionPath}
                placeholder={urlSearch ? 'Filter pages' : '/path'}
                onChange={(event) => {
                  setPinnedQuery('')
                  setForm((current) => ({
                    ...current,
                    actionPath: event.target.value,
                  }))
                }}
              />
              {urlSearch && (
                <div className="wn-url-suggestions">
                  <div className="wn-field__label">Start from</div>
                  <div className="wn-url-suggestions__list">
                    {filteredSuggestions.length === 0 ? (
                      <p className="wn-url-suggestions__empty">
                        That URL does not exist
                      </p>
                    ) : (
                      filteredSuggestions.map((path) => (
                        <Button
                          key={path}
                          type="button"
                          variant="light"
                          className={
                            path === form.actionPath
                              ? 'wn-url-suggestions__item is-selected'
                              : 'wn-url-suggestions__item'
                          }
                          onClick={() => {
                            const query = form.actionPath.trim()
                            const exact = suggestions.some(
                              (item) =>
                                item.toLowerCase() === query.toLowerCase()
                            )
                            setPinnedQuery(
                              !query || exact ? '' : form.actionPath
                            )
                            setForm((current) => ({
                              ...current,
                              actionPath: path,
                            }))
                          }}
                        >
                          {path}
                        </Button>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
            <div className="wn-field">
              <div className="wn-field__label">Availability</div>
              <div className="wn-pills">
                {AVAILABILITY_OPTIONS.map((option) => (
                  <Button
                    key={option}
                    type="button"
                    variant="light"
                    className={
                      form.availability === option ? 'wn-pill is-on' : 'wn-pill'
                    }
                    onClick={() =>
                      setForm((current) => ({
                        ...current,
                        availability: option,
                      }))
                    }
                  >
                    {option}
                  </Button>
                ))}
              </div>
            </div>
            <div className="wn-field">
              <div className="wn-field__label">
                Who can see it
                <span>Staff always see every feature</span>
              </div>
              <div className="wn-pills">
                {AUDIENCES.map((audience) => (
                  <Button
                    key={audience}
                    type="button"
                    variant="light"
                    className={
                      form.audiences.includes(audience)
                        ? 'wn-pill is-on'
                        : 'wn-pill'
                    }
                    onClick={() =>
                      setForm((current) => ({
                        ...current,
                        audiences: current.audiences.includes(audience)
                          ? current.audiences.filter(
                              (item) => item !== audience
                            )
                          : [...current.audiences, audience],
                      }))
                    }
                  >
                    {audience}
                  </Button>
                ))}
              </div>
            </div>
            <div className="wn-tour">
              <div>
                <div className="wn-tour__title">Include in arrival tour</div>
                <div className="wn-tour__note">
                  {form.includeInTour
                    ? 'Shows in the arrival tour and the feed.'
                    : 'Feed only. It will not appear in the arrival tour.'}
                </div>
              </div>
              <Button
                type="button"
                variant="light"
                className={form.includeInTour ? 'wn-switch is-on' : 'wn-switch'}
                aria-pressed={form.includeInTour}
                aria-label="Include in arrival tour"
                onClick={() =>
                  setForm((current) => ({
                    ...current,
                    includeInTour: !current.includeInTour,
                  }))
                }
              >
                <span />
              </Button>
            </div>
          </section>
        </div>
        <aside className="wn-checks">
          <div className="wn-checks__head">
            <span>Checks</span>
            {ready ? (
              <span className="wn-ready">Ready</span>
            ) : (
              <span className="wn-checks__left">{failCount} to fix</span>
            )}
          </div>
          <ul>
            {checks.map((check) => (
              <li key={check.id}>
                <span className={check.pass ? 'is-pass' : 'is-fail'}>
                  {check.pass ? '✓' : '×'}
                </span>
                {check.label}
              </li>
            ))}
          </ul>
          <div className="wn-checks__foot">
            <div className="wn-publish">
              <p>Publishes to</p>
              <strong>{formatReleaseDate(destination.publishedOn)}</strong>
              <span>{releaseNote}</span>
            </div>
            {message?.variant === 'success' && (
              <div className="wn-added">
                <strong>
                  {message.saved
                    ? `Saved "${message.title}".`
                    : `Added "${message.title}" on ${formatReleaseDate(message.publishedOn)}.`}
                </strong>
                <span>
                  {message.saved
                    ? 'The published date stays the same.'
                    : 'Admins see it on their next visit.'}
                </span>
              </div>
            )}
            {message?.variant === 'danger' && (
              <p className="wn-error">{message.text}</p>
            )}
            <Button
              type="button"
              className="wn-submit"
              disabled={!ready || saving || unchanged}
              aria-busy={saving}
              onClick={publish}
            >
              <span
                className={
                  saving ? 'wn-submit__label is-hidden' : 'wn-submit__label'
                }
              >
                {editingSlug ? 'Save changes' : 'Add to feed'}
              </span>
              {saving && <Spinner />}
            </Button>
          </div>
        </aside>
        <aside className="wn-preview">
          <div className="wn-preview__head">
            <span>As admins see it</span>
            <div className="wn-tabs">
              <Button
                type="button"
                variant="light"
                className={previewMode === 'tour' ? 'is-on' : ''}
                onClick={() => setPreviewMode('tour')}
              >
                Tour step
              </Button>
              <Button
                type="button"
                variant="light"
                className={previewMode === 'feed' ? 'is-on' : ''}
                onClick={() => setPreviewMode('feed')}
              >
                Feed card
              </Button>
            </div>
          </div>
          {previewMode === 'tour' ? (
            <div className="whats-new-modal__content wn-preview__tour">
              <TourStep
                className="wn-preview__step"
                feature={previewFeature}
                mode="tour"
                stepLabel={`What's new · ${formatReleaseDate(destination.publishedOn)} · 1 of 1`}
                imageIndex={imageIndex}
                onImageIndex={setImageIndex}
                stepIndex={0}
                stepCount={1}
                showClose={false}
                primaryLabel="Done"
              />
            </div>
          ) : (
            <FeedCard feature={previewFeature} />
          )}
          <p className="wn-preview__note">
            {form.includeInTour
              ? 'Shows in the arrival tour and the feed.'
              : 'Feed only. It will not appear in the arrival tour.'}
          </p>
        </aside>
      </div>
    </div>
  )
}
